// INC2-CONTACT: the Connections UI's grant reads and labels (integrations design §2.6, §2.10).
// A grant names who may use a connection (a person or a DE by membership, a seat, a team, or a
// person's own crew), how far (read, act, manage), the tool families it opens and its limits.
import type { OperationOutput } from "@companyos/sdk";
import { type WorkerKind, workerName } from "@companyos/ui";
import { infiniteQueryOptions, useInfiniteQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import type { Api } from "../../api/client";
import { digitalEmployeesQuery, peopleQuery, seatsQuery } from "../queries";

export type Grant = OperationOutput<"connectionGrants.list">["data"][number];
export type GrantLevel = Grant["level"];
export type GranteeKind = Grant["grantee_kind"];
export type Family = Grant["families"][number];

export const LEVELS: readonly GrantLevel[] = ["use_read", "use_act", "manage"];
export const LEVEL_LABEL: Record<GrantLevel, string> = {
  use_read: "Read",
  use_act: "Act",
  manage: "Manage",
};
/** What each level lets its holder do (behind an (i)). */
export const LEVEL_INFO: Record<GrantLevel, string> = {
  use_read: "Reads through the connection. Every act tool is refused.",
  use_act: "Reads and acts (sends, writes). Each act is approved first unless limits allow it.",
  manage: "Reads, acts and grants it to others.",
};
export const FAMILIES: readonly Family[] = ["mail", "calendar", "crm", "message", "api"];
export const FAMILY_LABEL: Record<Family, string> = {
  mail: "Mail",
  calendar: "Calendar",
  crm: "CRM",
  message: "Messages",
  api: "API",
};

export const PROVIDER_NAME: Record<string, string> = {
  api_key: "API key",
  google: "Google",
  github: "GitHub",
  slack: "Slack",
};

export const grantKeys = {
  all: (co: string) => ["company", co, "connection-grants"] as const,
  list: (co: string, f: { connection?: string; holder?: string }) =>
    ["company", co, "connection-grants", f.connection ?? "", f.holder ?? ""] as const,
};

/** Live grants of the connections the caller manages (one connection's, or one holder's). */
export const grantsQuery = (api: Api, co: string, f: { connection?: string; holder?: string }) => ({
  queryKey: grantKeys.list(co, f),
  queryFn: () =>
    api.client.connectionGrants.listAll({
      params: { co },
      query: {
        ...(f.connection ? { connection: f.connection } : {}),
        ...(f.holder ? { holder: f.holder } : {}),
      },
    }),
});

export type Grantee = { name: string; worker: WorkerKind | null; kind: GranteeKind };

/**
 * Names for grantees: people and DEs by membership, seats by title, teams by name; a crew is the
 * owner's own agent. Unknown ids read as their kind (a row the caller cannot list).
 */
export function useGrantees(api: Api, co: string, enabled = true) {
  // Every page (Codex r2 #7): a picker or a name must reach the 201st person, seat or team
  const people = useAllPages(useInfiniteQuery({ ...peopleQuery(api, co), enabled }), enabled);
  const des = useAllPages(
    useInfiniteQuery({ ...digitalEmployeesQuery(api, co), enabled }),
    enabled,
  );
  const seats = useAllPages(useInfiniteQuery({ ...seatsQuery(api, co), enabled }), enabled);
  const teams = useAllPages(
    useInfiniteQuery(
      infiniteQueryOptions({
        queryKey: ["company", co, "teams", "grantees"],
        initialPageParam: undefined as string | undefined,
        queryFn: ({ pageParam }) =>
          api.client.teams.list({
            params: { co },
            query: { limit: 200, ...(pageParam ? { cursor: pageParam } : {}) },
          }),
        getNextPageParam: (p) => p.next_cursor ?? undefined,
        enabled,
      }),
    ),
    enabled,
  );
  const all = [people, des, seats, teams];
  /** Still reading (a first page, or further pages): pickers say so rather than look empty. */
  const loading = enabled && all.some((q) => q.isPending || q.hasNextPage);
  const error = all.find((q) => q.isError)?.error ?? null;
  const byId = new Map<string, Grantee>();
  for (const p of people.data?.pages.flatMap((x) => x.data) ?? [])
    byId.set(`membership:${p.id}`, { name: p.display_name, worker: "person", kind: "membership" });
  for (const d of des.data?.pages.flatMap((x) => x.data) ?? [])
    byId.set(`membership:${d.membership_id}`, {
      name: workerName(d.display_name),
      worker: "de",
      kind: "membership",
    });
  for (const s of seats.data?.pages.flatMap((x) => x.data) ?? [])
    byId.set(`seat:${s.id}`, { name: s.title, worker: null, kind: "seat" });
  const teamRows = teams.data?.pages.flatMap((x) => x.data) ?? [];
  for (const t of teamRows) byId.set(`team:${t.id}`, { name: t.name, worker: null, kind: "team" });
  const of = (g: Pick<Grant, "grantee_kind" | "grantee_id">): Grantee =>
    byId.get(`${g.grantee_kind}:${g.grantee_id}`) ??
    (g.grantee_kind === "crew"
      ? { name: "Personal agent", worker: "ag", kind: "crew" }
      : loading
        ? { name: "…", worker: null, kind: g.grantee_kind }
        : { name: `Unknown ${g.grantee_kind}`, worker: null, kind: g.grantee_kind });
  return {
    of,
    loading,
    error,
    people: people.data?.pages.flatMap((x) => x.data) ?? [],
    des: des.data?.pages.flatMap((x) => x.data) ?? [],
    seats: seats.data?.pages.flatMap((x) => x.data) ?? [],
    teams: teamRows,
  };
}

/**
 * Follows a paged query's cursors to the end: only while enabled, never during another fetch (a
 * background refetch is not cancelled), and it stops on an error (Codex r5 #1).
 */
export function useAllPages<
  Q extends {
    hasNextPage: boolean;
    isFetching: boolean;
    isError: boolean;
    fetchNextPage: (o?: { cancelRefetch?: boolean }) => unknown;
  },
>(q: Q, enabled = true): Q {
  const { hasNextPage, isFetching, isError, fetchNextPage } = q;
  useEffect(() => {
    if (enabled && hasNextPage && !isFetching && !isError)
      void fetchNextPage({ cancelRefetch: false });
  }, [enabled, hasNextPage, isFetching, isError, fetchNextPage]);
  return q;
}

/**
 * The name of a connection a grant is on: the caller's own list first, then what the grants read
 * carries for a connection the caller manages (a delegated manager cannot list company
 * connections); else "Restricted connection".
 */
export function connectionName(
  id: string,
  listed: ReadonlyMap<string, { label: string; provider: string }>,
  managed: readonly ManagedConnection[] | undefined,
): { label: string; provider: string | null; account: string | null } {
  const l = listed.get(id);
  const m = managed?.find((c) => c.id === id);
  if (l) return { label: l.label, provider: l.provider, account: m?.identity_display ?? null };
  if (m) return { label: m.label, provider: m.provider, account: m.identity_display };
  return { label: "Restricted connection", provider: null, account: null };
}

export type ManagedConnection = OperationOutput<"connectionGrants.listAll">["connections"][number];

/** One line for a grant's limits ("20 acts a day · client.test"), or null. */
export function limitsText(l: Grant["limits"]): string | null {
  const parts = [
    l.daily_acts ? `${l.daily_acts} acts a day` : null,
    l.recipient_domains?.length ? l.recipient_domains.join(", ") : null,
  ].filter(Boolean);
  return parts.length ? parts.join(" · ") : null;
}

/** "Mail, CRM", or "All families" for an empty list. */
export const familiesText = (f: readonly Family[]) =>
  f.length ? f.map((x) => FAMILY_LABEL[x]).join(", ") : "All families";
